import { createApiClient } from './client'

export type SubscriptionTier = 'free' | 'pro'

export interface AdminMeResponse {
  success: boolean
  data: { ok: boolean; email: string }
}

export interface AdminSubscriptionResponse {
  success: boolean
  data: {
    tier: SubscriptionTier
    limits: { tripLimit: number; crewLimit: number; vehicleLimit: number; photoLimit: number }
    features: Record<string, boolean>
  }
}

export interface AdminUserResponse {
  success: boolean
  data: {
    id: string
    email: string
    full_name: string | null
    created_at: string | null
    profile: {
      experience_level: string
      bio: string | null
      location: string | null
      years_overlanding: number
      profile_image_path: string | null
      created_at: string
      updated_at: string
    } | null
  }
}

export interface AdminSubscriptionUpdateBody {
  tier: SubscriptionTier
}

export interface AdminTripsResponse {
  success: boolean
  data: AdminTrip[]
  pagination?: { page: number; limit: number; total: number }
}

export interface AdminTrip {
  id: string
  name: string
  description?: string | null
  start_date?: string | null
  end_date?: string | null
  is_public: boolean
  created_by: string
  created_at: string
  updated_at: string
  archived_at?: string | null
  member_count?: number
  cover_photo_url?: string | null
}

export interface AdminWaypoint {
  id: string
  name: string
  latitude?: number | null
  longitude?: number | null
  notes?: string | null
  poi_type?: string | null
  created_at?: string
  trip_day?: number | null
  order_index?: number
  start_time?: string | null
  end_time?: string | null
  is_poi?: boolean
  junction_id?: string
}

export interface AdminTripWaypointsResponse {
  success: boolean
  data: AdminWaypoint[]
}

export interface AdminTripMeal {
  id: string
  trip_id: string
  name: string
  description?: string | null
  meal_type?: string | null
  planned_date?: string | null
  planned_meal_time?: string | null
  serving_size?: number | null
  created_at?: string
  updated_at?: string
  [key: string]: unknown
}

export interface AdminTripAnnouncement {
  id: string
  trip_id: string
  author_id: string | null
  content: string
  created_at: string
  updated_at: string
}

export interface AdminTripGear {
  id: string
  trip_id: string
  name: string
  category?: string | null
  quantity?: number | null
  notes?: string | null
  created_at?: string
  updated_at?: string
  [key: string]: unknown
}

export interface AdminTripMealsResponse {
  success: boolean
  data: AdminTripMeal[]
}

export interface AdminTripAnnouncementsResponse {
  success: boolean
  data: AdminTripAnnouncement[]
}

export interface AdminTripGearResponse {
  success: boolean
  data: AdminTripGear[]
}

export interface AdminTripResponse {
  success: boolean
  data: AdminTrip
}

export function createAdminApi(getAuthToken: () => Promise<string | null>) {
  const client = createApiClient(getAuthToken)

  return {
    async getAdminMe(): Promise<AdminMeResponse> {
      return client.get<AdminMeResponse>('/admin/me')
    },

    async getUser(userId: string): Promise<AdminUserResponse> {
      return client.get<AdminUserResponse>(`/admin/users/${userId}`)
    },

    async getUserSubscription(userId: string): Promise<AdminSubscriptionResponse> {
      return client.get<AdminSubscriptionResponse>(`/admin/users/${userId}/subscription`)
    },

    async updateUserSubscription(
      userId: string,
      body: AdminSubscriptionUpdateBody
    ): Promise<{ success: boolean; data: unknown }> {
      return client.patch<{ success: boolean; data: unknown }>(
        `/admin/users/${userId}/subscription`,
        body
      )
    },

    async getUserTrips(
      userId: string,
      params?: { page?: number; limit?: number; include_archived?: boolean }
    ): Promise<AdminTripsResponse> {
      const searchParams = new URLSearchParams()
      if (params?.page != null) searchParams.set('page', String(params.page))
      if (params?.limit != null) searchParams.set('limit', String(params.limit))
      if (params?.include_archived !== undefined)
        searchParams.set('include_archived', String(params.include_archived))
      const query = searchParams.toString()
      return client.get<AdminTripsResponse>(
        `/admin/users/${userId}/trips${query ? `?${query}` : ''}`
      )
    },

    async getTrip(tripId: string): Promise<AdminTripResponse> {
      return client.get<AdminTripResponse>(`/admin/trips/${tripId}`)
    },

    async getTripWaypoints(tripId: string): Promise<AdminTripWaypointsResponse> {
      return client.get<AdminTripWaypointsResponse>(`/admin/trips/${tripId}/waypoints`)
    },

    async getTripMeals(tripId: string): Promise<AdminTripMealsResponse> {
      return client.get<AdminTripMealsResponse>(`/admin/trips/${tripId}/meals`)
    },

    async getTripAnnouncements(tripId: string): Promise<AdminTripAnnouncementsResponse> {
      return client.get<AdminTripAnnouncementsResponse>(`/admin/trips/${tripId}/announcements`)
    },

    async getTripGear(tripId: string): Promise<AdminTripGearResponse> {
      return client.get<AdminTripGearResponse>(`/admin/trips/${tripId}/gear`)
    },
  }
}

export type AdminApi = ReturnType<typeof createAdminApi>
